import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ChevronRight,
  Loader2,
  AlertTriangle,
  Database,
  Phone,
  Calendar,
  Users,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Megaphone,
  Globe,
  UserPlus,
  PhoneCall,
  HelpCircle,
} from "lucide-react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";

// 5-bucket source taxonomy — mirror of DEAL_SOURCES in
// backend/marketing_analytics.py. Order matches the visual order of the
// chips strip on the page.
const SOURCE_BUCKETS = [
  { key: "paid_ads",       label: "Paid ads",       icon: Megaphone,  accent: "text-orange-300", chipBg: "bg-orange-500/20 text-orange-200 border-orange-500/40" },
  { key: "organic_social", label: "Organic / social", icon: Globe,     accent: "text-emerald-300", chipBg: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" },
  { key: "referral",       label: "Referral",       icon: UserPlus,   accent: "text-sky-300", chipBg: "bg-sky-500/20 text-sky-200 border-sky-500/40" },
  { key: "offline",        label: "Manual / CRM",   icon: PhoneCall,  accent: "text-zinc-300", chipBg: "bg-zinc-500/20 text-zinc-200 border-zinc-500/40" },
  { key: "unknown",        label: "Unknown",        icon: HelpCircle, accent: "text-amber-300", chipBg: "bg-amber-500/20 text-amber-200 border-amber-500/40" },
];

function calendarMonthRange(anchorMonthsAgo = 1) {
  const now = new Date();
  const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - anchorMonthsAgo, 1));
  const first = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), 1));
  let last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0));
  if (anchorMonthsAgo === 0 && last > now) last = new Date(now);
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

function _initials(name) {
  if (!name) return "?";
  const parts = String(name).split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

function _avatarBg(name) {
  const palette = [
    "bg-emerald-500/30 text-emerald-100",
    "bg-cyan-500/30 text-cyan-100",
    "bg-purple-500/30 text-purple-100",
    "bg-amber-500/30 text-amber-100",
    "bg-pink-500/30 text-pink-100",
    "bg-blue-500/30 text-blue-100",
  ];
  let h = 0;
  for (let i = 0; i < String(name || "").length; i++) h = (h * 31 + String(name).charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function scoreColor(n) {
  if (n == null) return "text-zinc-500";
  if (n >= 70) return "text-emerald-300";
  if (n >= 50) return "text-amber-300";
  return "text-red-300";
}
function barColor(n) {
  if (n == null) return "bg-zinc-700";
  if (n >= 70) return "bg-emerald-400";
  if (n >= 50) return "bg-amber-400";
  return "bg-red-400";
}

function DeltaChip({ delta, size = "sm" }) {
  if (delta == null) return null;
  const cls = delta > 0 ? "text-emerald-400" : delta < 0 ? "text-red-400" : "text-zinc-500";
  const sign = delta > 0 ? "+" : "";
  const sz = size === "lg" ? "text-base" : size === "md" ? "text-sm" : "text-[10px]";
  return <sup className={`${cls} ${sz} font-semibold ml-0.5`}>{sign}{delta}</sup>;
}

// One metric row — bar + score + tooltip "In X% of N leads, he ..."
function MetricRow({ m, ownerFirstName }) {
  const score = m.score;
  const width = score != null ? `${Math.max(2, score)}%` : "0%";
  const usage = m.count_used > 0
    ? `In ${m.pct_pass}% of ${m.count_used} lead${m.count_used === 1 ? "" : "s"}, ${ownerFirstName || "he"} ${m.hint}.`
    : `No data — ${ownerFirstName || "he"} hasn't been observed on this metric yet.`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-200">{m.label}</span>
            <span className={`font-bold ${scoreColor(score)}`}>
              {score ?? "—"}
              <DeltaChip delta={m.score_delta} />
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full bg-zinc-800 rounded overflow-hidden">
            <div className={`h-full ${barColor(score)}`} style={{ width }} />
          </div>
          <div className="mt-0.5 flex justify-between text-[9px] text-zinc-600">
            <span>0</span>
            <span>{m.count_used > 0 && <>{m.count_used} sampled</>}</span>
            <span>100</span>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="text-xs">{usage}</div>
      </TooltipContent>
    </Tooltip>
  );
}

function BucketColumn({ title, icon: Icon, accent, average, averageDelta, metrics, tooltip, ownerFirstName }) {
  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className={`pb-2 ${accent}`}>
        <CardTitle className="text-zinc-100 text-base inline-flex items-center gap-2">
          <Icon className="w-4 h-4" /> {title}
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-zinc-400 cursor-help text-[10px]">ⓘ</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs"><div className="text-xs">{tooltip}</div></TooltipContent>
            </Tooltip>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-zinc-500 text-[11px] uppercase tracking-wider">Average Score</span>
            <span className={`text-3xl font-bold ${scoreColor(average)}`}>
              {average ?? "—"}
              <DeltaChip delta={averageDelta} size="md" />
            </span>
          </div>
        </div>
        <div className="space-y-3">
          {(metrics || []).map((m) => (
            <MetricRow key={m.key} m={m} ownerFirstName={ownerFirstName} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MarketingPersonProfilePage() {
  const { owner_id } = useParams();
  const [searchParams] = useSearchParams();
  const initFrom = searchParams.get("from");
  const initTo = searchParams.get("to");
  const initSource = searchParams.get("source") || "";
  const tm = calendarMonthRange(0);
  const [dateFrom, setDateFrom] = useState(initFrom || tm.from);
  const [dateTo, setDateTo] = useState(initTo || tm.to);
  const [monthAnchor, setMonthAnchor] = useState(initFrom ? null : 0);
  // Active source filter (paid_ads | organic_social | referral | offline | unknown | "" = all).
  // Re-fetches manager profile when changed — backend recomputes buckets
  // using only summaries from that deal_source.
  const [sourceFilter, setSourceFilter] = useState(initSource);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async () => {
    setLoading(true); setError("");
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (sourceFilter) params.source = sourceFilter;
      const res = await api.get(`/marketing-analytics/manager-profiles/${encodeURIComponent(owner_id)}`, {
        params,
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner_id, dateFrom, dateTo, sourceFilter]);

  const firstName = (data?.owner_name || "").split(/\s+/)[0] || "they";

  return (
    <TooltipProvider delayDuration={150}>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-2 text-sm">
          <Link to="/marketing-analytics/people" className="text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1">
            <ChevronRight className="w-3 h-3 rotate-180" /> People
          </Link>
          <span className="text-zinc-600">/</span>
          <span className="text-zinc-300">{data?.owner_name || "Profile"}</span>
        </div>

        {error && (
          <Card className="bg-red-900/20 border-red-800">
            <CardContent className="py-3 text-red-200 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </CardContent>
          </Card>
        )}

        {loading && !data && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-8 text-center text-zinc-400 text-sm inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading profile…
            </CardContent>
          </Card>
        )}

        {data && data.no_data && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-8 text-center text-zinc-500 text-sm">
              No analyzed leads for {data.owner_name} in this period.
            </CardContent>
          </Card>
        )}

        {/* Period switcher — preset chips + month stepper + inline pickers,
            same UX as the People list page. Visible whenever data is loaded. */}
        {data && (
          <div className="flex items-center gap-2 flex-wrap text-xs bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
            <span className="text-zinc-500 uppercase tracking-wider">Period</span>
            {[{ months: 0, label: "This month" }, { months: 1, label: "Last month" }].map((p) => {
              const r = calendarMonthRange(p.months);
              const active = monthAnchor === p.months;
              return (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => { setMonthAnchor(p.months); setDateFrom(r.from); setDateTo(r.to); }}
                  className={`px-2.5 py-1 rounded border transition ${
                    active ? "bg-blue-500/20 text-blue-200 border-blue-500/40" : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
            <div className="inline-flex items-center bg-zinc-900/60 border border-zinc-800 rounded">
              <button type="button"
                onClick={() => {
                  const next = (monthAnchor == null ? 1 : monthAnchor) + 1;
                  const r = calendarMonthRange(next);
                  setMonthAnchor(next); setDateFrom(r.from); setDateTo(r.to);
                }}
                className="px-2 py-1 text-zinc-400 hover:text-zinc-100 border-r border-zinc-800"
              >
                <ChevronRight className="w-3 h-3 rotate-180" />
              </button>
              <span className="px-2 py-1 text-zinc-300 min-w-[110px] text-center">
                {monthAnchor != null && monthAnchor >= 0
                  ? (() => {
                      const d = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth() - monthAnchor, 1));
                      return d.toLocaleString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
                    })()
                  : "Custom range"}
              </span>
              <button type="button"
                onClick={() => {
                  const next = Math.max(0, (monthAnchor == null ? 1 : monthAnchor) - 1);
                  const r = calendarMonthRange(next);
                  setMonthAnchor(next); setDateFrom(r.from); setDateTo(r.to);
                }}
                disabled={monthAnchor === 0}
                className="px-2 py-1 text-zinc-400 hover:text-zinc-100 disabled:opacity-30 border-l border-zinc-800"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
            <div className="ml-auto flex items-center gap-1.5 text-zinc-400">
              <Input type="date" value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setMonthAnchor(null); }}
                className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
              />
              <span className="text-zinc-600">→</span>
              <Input type="date" value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setMonthAnchor(null); }}
                className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
              />
            </div>
          </div>
        )}

        {data && !data.no_data && (
          <>
            {/* Header card — avatar + Overall Score + secondary stats */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6">
                <div className="flex items-start gap-5 flex-wrap">
                  <div className={`shrink-0 w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold ${_avatarBg(data.owner_name)}`}>
                    {_initials(data.owner_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl font-bold text-zinc-50">{data.owner_name}</h1>
                    <div className="text-zinc-500 text-sm">
                      Sales · Seller
                      <span className="ml-2 text-zinc-600">·</span>
                      <span className="ml-2">{data.owner_email}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div className="bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2">
                        <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Graded</div>
                        <div className="text-zinc-100 text-xl font-bold">
                          {data.lead_count} <span className="text-zinc-500 text-sm">/ {data.total_lead_count ?? data.lead_count}</span>
                        </div>
                        <div className="text-zinc-600 text-[10px] mt-0.5">in period</div>
                      </div>
                      <div className="bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2">
                        <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Meetings held</div>
                        <div className="text-zinc-100 text-xl font-bold">{data.meetings_count || 0}</div>
                      </div>
                      <div className="bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2">
                        <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Unreachable</div>
                        <div className="text-zinc-300 text-xl font-bold">{data.unreachable_count || 0}</div>
                      </div>
                      <div className="bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2">
                        <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Period</div>
                        <div className="text-zinc-200 text-sm font-medium font-mono">
                          {dateFrom} → {dateTo}
                        </div>
                        {data.previous_range && (
                          <div className="text-zinc-600 text-[10px] mt-0.5">
                            vs {data.previous_range.date_from} → {data.previous_range.date_to}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-6xl font-bold ${scoreColor(data.overall_score)}`}>
                      {data.overall_score ?? "—"}
                      <DeltaChip delta={data.overall_score_delta} size="lg" />
                    </div>
                    <div className="text-zinc-500 text-[11px] uppercase tracking-wider mt-1">
                      Overall Sales Score
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Source filter — split this manager's leads across 5 channels
                so the user can compare paid-ads performance vs offline /
                referral / etc. Each chip shows graded/total. Click toggles
                the source filter (re-fetches profile with ?source=). */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-zinc-300 text-sm font-medium">Lead sources</div>
                    <div className="text-zinc-500 text-xs mt-0.5">
                      Click to see this manager's performance for one channel only.
                      "Graded / Total" — only graded leads feed Profile Skills.
                      Pending leads are auto-analyzed every 6h (cap 80/day).
                    </div>
                  </div>
                  {sourceFilter && (
                    <button
                      type="button"
                      onClick={() => setSourceFilter("")}
                      className="text-xs px-2 py-1 rounded border border-zinc-700 bg-zinc-900/60 text-zinc-300 hover:text-zinc-50 hover:border-zinc-600"
                    >
                      Show all sources
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {SOURCE_BUCKETS.map((s) => {
                    const total = (data.total_by_source || {})[s.key] || 0;
                    const graded = (data.graded_by_source || {})[s.key] || 0;
                    const active = sourceFilter === s.key;
                    const Icon = s.icon;
                    const disabled = total === 0;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => setSourceFilter(active ? "" : s.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition ${
                          active
                            ? `${s.chipBg} ring-1 ring-offset-1 ring-offset-zinc-950 ring-white/20`
                            : disabled
                              ? "bg-zinc-900/40 text-zinc-600 border-zinc-800 cursor-not-allowed"
                              : "bg-zinc-900/60 text-zinc-300 border-zinc-700 hover:border-zinc-500 hover:text-zinc-50"
                        }`}
                      >
                        <Icon className={`w-3.5 h-3.5 ${active || disabled ? "" : s.accent}`} />
                        <span>{s.label}</span>
                        <span className={`ml-1 tabular-nums ${active ? "" : "text-zinc-500"}`}>
                          {graded}/{total}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {sourceFilter && (
                  <div className="mt-3 text-xs text-zinc-400 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3 text-zinc-500" />
                    Profile below shows only{" "}
                    <span className="text-zinc-200 font-medium">
                      {SOURCE_BUCKETS.find((x) => x.key === sourceFilter)?.label}
                    </span>{" "}
                    leads ({data.lead_count} graded).
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Profile Skills — three buckets, mirrors the requested design */}
            <div>
              <h2 className="text-xl font-bold text-zinc-50 mb-3">Profile Skills</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <BucketColumn
                  title={data.buckets?.crm?.label || "CRM"}
                  tooltip={data.buckets?.crm?.tooltip}
                  icon={Database}
                  accent="border-b border-zinc-800"
                  average={data.buckets?.crm?.average}
                  averageDelta={data.buckets?.crm?.average_delta}
                  metrics={data.buckets?.crm?.metrics}
                  ownerFirstName={firstName}
                />
                <BucketColumn
                  title={data.buckets?.calls?.label || "Calls"}
                  tooltip={data.buckets?.calls?.tooltip}
                  icon={Phone}
                  accent="border-b border-zinc-800"
                  average={data.buckets?.calls?.average}
                  averageDelta={data.buckets?.calls?.average_delta}
                  metrics={data.buckets?.calls?.metrics}
                  ownerFirstName={firstName}
                />
                <BucketColumn
                  title={data.buckets?.meetings?.label || "Meetings"}
                  tooltip={data.buckets?.meetings?.tooltip}
                  icon={Calendar}
                  accent="border-b border-zinc-800"
                  average={data.buckets?.meetings?.average}
                  averageDelta={data.buckets?.meetings?.average_delta}
                  metrics={data.buckets?.meetings?.metrics}
                  ownerFirstName={firstName}
                />
              </div>
            </div>

            {/* Examples — best and worst lead from this manager in the window */}
            {data.examples && (data.examples.best?.length > 0 || data.examples.worst?.length > 0) && (
              <div>
                <h2 className="text-xl font-bold text-zinc-50 mb-3">Evidence</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.examples.best?.length > 0 && (
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-emerald-300 text-sm inline-flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Best handled
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {data.examples.best.map((e) => (
                          <Link key={e.deal_id} to={`/marketing-analytics?focus=${encodeURIComponent(e.deal_id)}`} className="block bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2 hover:border-zinc-700 transition">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-zinc-100 text-sm truncate flex-1">{e.deal_name || "(unnamed)"}</span>
                              <span className={`font-mono font-bold text-sm ${scoreColor(e.sales_score)}`}>{e.sales_score}</span>
                            </div>
                            {e.campaign && <div className="text-[10px] text-zinc-500 truncate">{e.campaign}</div>}
                            {e.coaching_feedback_head && <div className="text-[11px] text-zinc-400 mt-1 italic line-clamp-2">{e.coaching_feedback_head}</div>}
                          </Link>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  {data.examples.worst?.length > 0 && (
                    <Card className="bg-zinc-900/50 border-zinc-800">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-red-300 text-sm inline-flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" /> Most coaching opportunity
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {data.examples.worst.map((e) => (
                          <Link key={e.deal_id} to={`/marketing-analytics?focus=${encodeURIComponent(e.deal_id)}`} className="block bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2 hover:border-zinc-700 transition">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-zinc-100 text-sm truncate flex-1">{e.deal_name || "(unnamed)"}</span>
                              <span className={`font-mono font-bold text-sm ${scoreColor(e.sales_score)}`}>{e.sales_score}</span>
                            </div>
                            {e.campaign && <div className="text-[10px] text-zinc-500 truncate">{e.campaign}</div>}
                            {e.headline_bottleneck && <div className="text-[11px] text-red-300/80 mt-1 italic">Bottleneck: {e.headline_bottleneck}</div>}
                            {e.coaching_feedback_head && <div className="text-[11px] text-zinc-400 mt-1 italic line-clamp-2">{e.coaching_feedback_head}</div>}
                          </Link>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
